import { DocumentNode, print } from 'graphql';
import { clear, createStore, del, set, values } from 'idb-keyval';
import { getValue, keys, removeValue } from 'services/idb-store';
import { logError } from 'services/logging';
import { CombinedError, OperationResult } from 'urql';
import { v4 as uuidv4 } from 'uuid';
import { MutationMessages, restoreData } from 'utils/offlineQueryMutation';

export interface OutboxEntry {
  id: string;
  key: string;
  query: string;
  variables: Record<string, unknown>;
  messages?: MutationMessages;
  // Replayed with the mutation so the same cached queries are invalidated as when sent online.
  additionalTypenames?: string[];
  createdAt: number;
}

export type OutboxResult = Pick<OperationResult, 'data' | 'error'>;
type SendEntry = (entry: OutboxEntry) => Promise<OutboxResult>;
type OnSettled = (entry: OutboxEntry, result: OutboxResult) => void;

interface LegacyQueuedMutation {
  data: unknown;
  mutationNode: DocumentNode;
  responseMessage?: MutationMessages;
  timestamp: number;
}

// Its own database, so clearStore() on a service-worker update can't wipe unsent work.
const outboxStore = createStore('nile-outbox', 'mutations');
const DRAIN_LOCK_NAME = 'nile-outbox-drain';
const LEGACY_QUEUE_INDEX_KEY = 'mutation-queue';
const LEGACY_QUEUE_KEY_PREFIX = 'mutation-queue-';

let lastCreatedAt = 0;
let activeDrain: Promise<boolean> | undefined;
let legacyMigration: Promise<void> | undefined;

// Strictly increasing so two submits in the same millisecond keep their order.
const nextCreatedAt = () => (lastCreatedAt = Math.max(Date.now(), lastCreatedAt + 1));

// The JSON round trip stores exactly what goes over the wire (e.g. dayjs as an ISO string).
const writeEntry = (entry: Omit<OutboxEntry, 'id'>, id = uuidv4()) =>
  set(id, { ...entry, id, variables: JSON.parse(JSON.stringify(entry.variables)) }, outboxStore);

export const enqueueMutation = (entry: Omit<OutboxEntry, 'id' | 'createdAt'>) =>
  writeEntry({ ...entry, createdAt: nextCreatedAt() });

export const clearOutbox = () => clear(outboxStore).catch(() => {});

const oldestEntry = async () => {
  const entries = await values<OutboxEntry>(outboxStore);
  return entries.reduce<OutboxEntry | undefined>(
    (oldest, entry) => (!oldest || entry.createdAt < oldest.createdAt ? entry : oldest),
    undefined,
  );
};

// The server never took the change (unreachable, timeout, rate limit, 5xx), so keep it for a retry.
const isRetryable = ({ error }: OutboxResult) => {
  if (!error?.networkError || error.graphQLErrors.length) return false;
  const status: number | undefined = error.response?.status;
  return status === undefined || status >= 500 || status === 408 || status === 429;
};

// Builds before the outbox queued per screen in the shared store. Remove once those clients are gone.
const migrateLegacyQueue = async () => {
  const legacyKeys = (await keys()).map(String).filter((key) => key.startsWith(LEGACY_QUEUE_KEY_PREFIX));
  for (const legacyKey of legacyKeys) {
    const queue: LegacyQueuedMutation[] = restoreData(await getValue(legacyKey))?.queue ?? [];
    // Deterministic ids make a migration interrupted before removeValue safe to rerun.
    for (const [index, mutation] of queue.entries()) {
      await writeEntry(
        {
          key: legacyKey.slice(LEGACY_QUEUE_KEY_PREFIX.length),
          query: print(mutation.mutationNode),
          variables: { input: mutation.data },
          messages: mutation.responseMessage,
          createdAt: mutation.timestamp,
        },
        `${legacyKey}-${mutation.timestamp}-${index}`,
      );
    }
    await removeValue(legacyKey);
  }
  await removeValue(LEGACY_QUEUE_INDEX_KEY);
};

// Resolves to the result to report, or undefined when the entry should stay for a retry.
const sendEntry = async (send: SendEntry, entry: OutboxEntry): Promise<OutboxResult | undefined> => {
  try {
    const result = await send(entry);
    return isRetryable(result) ? undefined : result;
  } catch (error) {
    // A throw (e.g. an unparsable query) repeats on every retry, so fail the entry rather than block the queue.
    logError(error, { extra: { outboxKey: entry.key } });
    const networkError = error instanceof Error ? error : new Error(String(error));
    return { data: undefined, error: new CombinedError({ networkError }) };
  }
};

// Returns true once the outbox is empty; false leaves entries for a later retry.
const runDrain = async (send: SendEntry, onSettled: OnSettled) => {
  await (legacyMigration ??= migrateLegacyQueue().catch(logError));
  for (let entry = await oldestEntry(); entry; entry = await oldestEntry()) {
    const result = await sendEntry(send, entry);
    if (!result) return false;
    // Removed per entry, so a drain cut short never resends what already went through.
    await del(entry.id, outboxStore);
    onSettled(entry, result);
  }
  return true;
};

// Another tab holding the lock is already sending these, so report them as not yet drained.
const withDrainLock = async (run: () => Promise<boolean>) => {
  if (!navigator.locks) return run();
  return navigator.locks.request(DRAIN_LOCK_NAME, { ifAvailable: true }, async (lock) => (lock ? run() : false));
};

// Overlapping calls share one drain, so no entry is ever in flight twice.
export const drainOutbox = (send: SendEntry, onSettled: OnSettled): Promise<boolean> => {
  activeDrain ??= withDrainLock(() => runDrain(send, onSettled))
    .catch((error) => {
      logError(error);
      return false;
    })
    .finally(() => {
      activeDrain = undefined;
    });
  return activeDrain;
};
